/**
 * SpritePool — pre-allocated pool of PIXI.Sprite instances with
 * checkout/return semantics.
 *
 * # Why this exists
 *
 * Layer 4 (SpriteRenderer) creates a sprite for every CombatEntity that
 * spawns and destroys it when the entity despawns. At v1 entity densities
 * (50-100 concurrent entities) this is fine. At horde-mode densities
 * (Vampire Survivors / Brotato benchmarks: 500-2000+ entities, with
 * spawn/death churn every frame) the per-spawn `new Sprite()` allocation
 * + the per-death `sprite.destroy()` deallocation become measurable GC
 * pressure.
 *
 * The pool eliminates this by reusing sprite instances. Spawn checks out
 * a pre-allocated sprite from the free-list; despawn returns it. The
 * sprite's PixiJS-level state (texture, position, alpha, etc.) is reset
 * on checkout, not on return — this lets us defer expensive resets until
 * the sprite is actually needed.
 *
 * # When pooling matters
 *
 * Pool benefits scale with churn rate, not with peak concurrent count.
 * Even modest peaks (~200 entities) with rapid turnover (every entity
 * despawns and respawns within ~1s) produce significant GC pressure
 * without pooling. v1 launch may not hit horde-mode densities, but the
 * Aevon's Wake roadmap commits to Horde Mode at Phase 13, and pooling
 * needs to be built BEFORE that integration window — adding it later is
 * a cross-cutting refactor that touches every spawn/despawn path.
 *
 * # Pool sizing strategy
 *
 * We grow the pool dynamically rather than pre-allocating a hard cap.
 * `initialCapacity` allocates N sprites up front (cheap; reduces first-
 * combat allocation). When checkout finds the free-list empty, we
 * allocate one fresh sprite on demand and add it to the pool's
 * "owned" set. When return is called, the sprite goes back to the
 * free-list.
 *
 * `maxCapacity` (optional) caps growth: if we'd exceed it, checkout
 * throws. By default no cap (let the pool grow as needed). Horde Mode
 * may set this to a sane ceiling (e.g. 5000) to surface runaway-spawn
 * bugs early rather than letting them OOM.
 *
 * # State reset on checkout
 *
 * Every sprite is reset to a known clean state on checkout:
 *   - position (0, 0)
 *   - anchor (0.5, 1.0) — bottom-center, matching tile-renderer convention
 *   - alpha 1.0
 *   - rotation 0
 *   - scale (1, 1)
 *   - tint 0xFFFFFF (no tint)
 *   - cullable true (CullerPlugin handles off-screen sprites)
 *   - visible true
 *   - zIndex 0 (caller sets to entity-derived value)
 *   - filters: empty (Layer 4b adds palette filter on a per-entity basis)
 *   - texture: null (caller sets via animation state)
 *
 * This list is the contract: callers receive a sprite in this exact
 * state. If we add more sprite properties later, we add them to the
 * reset list.
 *
 * # Lifecycle
 *
 *   1. Construct pool with optional initialCapacity / maxCapacity
 *   2. Call checkout() to get a sprite; sprite is added to a parent
 *      Container by the caller
 *   3. Call returnSprite(sprite) to release it; sprite is removed from
 *      its parent (if any) and added back to the free-list
 *   4. Call dispose() at end of combat to destroy ALL pooled sprites
 *      (both checked-out and free) and release internal references
 *
 * # Test contract
 *
 *   - Construction with initialCapacity allocates N sprites up front
 *   - checkout returns sprites in clean state
 *   - returnSprite removes sprite from parent and returns to free-list
 *   - Pool grows dynamically when free-list empty
 *   - maxCapacity check throws when exceeded
 *   - dispose destroys all owned sprites
 *   - Idempotent: dispose called twice is a no-op
 *   - Returned sprite that's checked out again gets reset state
 *
 * @see {@link ./spriteRenderer} — Layer 4's primary consumer
 * @see {@link ./tileRenderer} — same anchor (0.5, 1.0) convention
 */

import { Sprite } from 'pixi.js';

/**
 * Configuration for SpritePool.
 */
export interface SpritePoolOptions {
  /**
   * Number of sprites to pre-allocate. Defaults to 0 (lazy allocation
   * on first checkout). Setting to a realistic peak-concurrent value
   * (~50 for v1, ~500 for horde mode) eliminates first-combat alloc
   * pause.
   */
  readonly initialCapacity?: number;

  /**
   * Optional hard cap on total pool size (free + checked-out). Throws
   * on checkout if exceeded. Defaults to undefined (no cap). Use to
   * surface runaway-spawn bugs in late-development testing; production
   * should stay uncapped.
   */
  readonly maxCapacity?: number;
}

/**
 * Per-sprite reset state. Applied on every checkout. Defines the
 * "clean slate" contract.
 */
function resetSpriteState(sprite: Sprite): void {
  sprite.position.set(0, 0);
  sprite.anchor.set(0.5, 1.0);
  sprite.alpha = 1.0;
  sprite.rotation = 0;
  sprite.scale.set(1, 1);
  sprite.tint = 0xffffff;
  sprite.cullable = true;
  sprite.visible = true;
  sprite.zIndex = 0;
  sprite.filters = [];
  // texture is not reset — caller sets it based on entity animation
}

/**
 * Pre-allocated sprite pool. Construct once per combat session;
 * dispose at session end.
 */
export class SpritePool {
  /** Sprites available for checkout. */
  private free: Sprite[] = [];
  /** All sprites this pool has ever owned (free + checked-out). */
  private owned: Set<Sprite> = new Set();
  private readonly maxCapacity: number | undefined;
  private disposed = false;

  constructor(options: SpritePoolOptions = {}) {
    this.maxCapacity = options.maxCapacity;
    const initialCapacity = options.initialCapacity ?? 0;

    if (initialCapacity > 0) {
      for (let i = 0; i < initialCapacity; i++) {
        const sprite = new Sprite();
        sprite.label = `pooled-sprite-${i}`;
        this.owned.add(sprite);
        this.free.push(sprite);
      }
    }
  }

  /**
   * Check out a sprite from the pool. Sprite is reset to clean state
   * before being returned. Caller is responsible for setting the
   * texture, position, zIndex, and adding to a parent Container.
   *
   * @throws Error if pool is disposed or maxCapacity would be exceeded
   */
  checkout(): Sprite {
    if (this.disposed) {
      throw new Error('SpritePool.checkout: pool has been disposed');
    }

    let sprite = this.free.pop();
    if (sprite === undefined) {
      // Free-list empty; grow the pool.
      if (
        this.maxCapacity !== undefined &&
        this.owned.size >= this.maxCapacity
      ) {
        throw new Error(
          `SpritePool.checkout: maxCapacity ${this.maxCapacity} exceeded ` +
            `(${this.owned.size} sprites currently owned)`,
        );
      }
      sprite = new Sprite();
      sprite.label = `pooled-sprite-${this.owned.size}`;
      this.owned.add(sprite);
    }

    resetSpriteState(sprite);
    return sprite;
  }

  /**
   * Return a sprite to the pool. Sprite is removed from its parent
   * Container (if attached) and placed back on the free-list. The
   * sprite's runtime state (texture, position, etc.) is NOT reset
   * here — reset happens on the next checkout. This defers work to
   * when it's actually needed.
   *
   * If the sprite is not owned by this pool, the call is a no-op
   * with a warn (defends against caller confusion across multiple
   * pools).
   */
  returnSprite(sprite: Sprite): void {
    if (this.disposed) {
      // After dispose, returned sprites are already destroyed; ignore.
      return;
    }
    if (!this.owned.has(sprite)) {
      console.warn(
        '[SpritePool.returnSprite] received sprite not owned by this pool; ignoring',
      );
      return;
    }
    if (this.free.includes(sprite)) {
      // Double-return is a programming error; warn but don't throw
      // (don't crash the game over a bookkeeping mistake).
      console.warn(
        '[SpritePool.returnSprite] sprite already in free-list; ignoring duplicate return',
      );
      return;
    }

    // Detach from parent if attached.
    if (sprite.parent !== null) {
      sprite.parent.removeChild(sprite);
    }

    this.free.push(sprite);
  }

  /**
   * Destroy all owned sprites (free + checked-out) and release
   * internal references. Idempotent.
   *
   * Note: callers must NOT use returned sprites after dispose. The
   * SpriteRenderer disposes its pool only at end-of-combat when no
   * entity is using a sprite.
   */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    for (const sprite of this.owned) {
      // Detach from any parent first.
      if (sprite.parent !== null) {
        sprite.parent.removeChild(sprite);
      }
      try {
        sprite.destroy({ children: false, texture: false, textureSource: false });
      } catch (err) {
        console.warn('[SpritePool.dispose] sprite.destroy threw:', err);
      }
    }

    this.free = [];
    this.owned.clear();
  }

  // ───────────────────────────────────────────────────────────────────────
  // Test introspection (do not use in production code)
  // ───────────────────────────────────────────────────────────────────────

  /** Test-only: number of sprites in the pool (free + checked-out). */
  _getOwnedCountForTests(): number {
    return this.owned.size;
  }

  /** Test-only: number of sprites available for checkout. */
  _getFreeCountForTests(): number {
    return this.free.length;
  }

  /** Test-only: number of currently-checked-out sprites. */
  _getCheckedOutCountForTests(): number {
    return this.owned.size - this.free.length;
  }

  /** Test-only: whether dispose has been called. */
  _isDisposedForTests(): boolean {
    return this.disposed;
  }
}
